// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useState } from 'react';
import { Layout } from 'antd';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { openMessage } from '@Helpers/notification';
import { handleNotificationError } from '@Helpers/notification';
import { Store } from 'antd/lib/form/interface';
import RouteBuilder from '@Services/RouteBuilder';
import { findDefaultPath } from '@Helpers/RedirectPath';
import { LoginSider } from '@Organisms/Login/LoginSider/LoginSider';
import { LoginContent } from '@Organisms/Login/LoginContent/LoginContent';
import client, { queryClient } from '@Client';
import { useGenericQuery } from '@Queries/genericQueries';
import { BrandingParams, StatusOutput } from 'thehive-sdk';
import { RouteType } from '@Types';
import { useStorageOptions } from '@Hooks/useStorageOptions';
import usePageTitle from '@Hooks/usePageTitle';
import { useLocalMessage } from '@Helpers/locale';

import './Login.scss';

const Login = (): ReactElement => {
    const history = useHistory();
    const { error } = useParams<{ error?: string }>();
    const { data: brandingData } = useGenericQuery<BrandingParams>({
        fetchOnStart: true,
        manual: true,
        retry: false,
        key: ['branding-props'],
        fetchCallback: useCallback(() => client.BrandingController.list(), []),
    });
    const i = useLocalMessage();

    usePageTitle(i('page-title.misc.login'), true);

    if (error) {
        openMessage('error', error);
    }

    const [mfa, setMFA] = useState<boolean>(false);

    const { data: dataStatus } = useGenericQuery<StatusOutput>({
        fetchOnStart: true,
        manual: true,
        retry: false,
        key: ['status-public'],
        fetchCallback: useCallback(() => client.StatusController.get('public'), []),
    });

    const { sso = false, ssoProviders = [] } = dataStatus || {};

    const { homepage } = useStorageOptions();

    const onError = ({ status }: any): void => {
        // If the user has set MFA authentication, the request will result with a
        // 402 status code, we catch it and add an input to enter MFA code
        if (status === 402) {
            setMFA(true);
        }
    };

    const onFinish = (values: Store): void => {
        const login = (values['login'] as string).toLowerCase();

        client.AuthController.login({
            user: login,
            password: values['password'],
            code: values['code'],
        })
            .then(({ organisation, permissions }) => {
                const defaultPath = findDefaultPath(permissions);

                const nextUrl = organisation === 'admin' ? RouteBuilder.compute(defaultPath as any) : homepage;
                history.push(nextUrl);
                queryClient.refetchQueries('currentUser');
            })
            .catch((err) => {
                if (err.status && err.status !== 402) {
                    handleNotificationError(err);
                }
                onError(err);
            });
    };

    const handleSsoLogin = (): void => {
        // window.location.href = window.location.origin + client.apiUrl + '/ssoLogin';
        window.location.href = window.location.origin + client.apiUrl + '/ssoLogin';
    };

    const onClickHandlerSSOProvider = (url: string): void => {
        window.location.href = url;
    };

    return (
        <Layout className="login-layout" key="login">
            <LoginSider src={brandingData?.loginLogo} />
            <LoginContent
                mfa={mfa}
                hasOauth={sso}
                ssoProviders={ssoProviders}
                onClickSSOProvider={onClickHandlerSSOProvider}
                onLoginFinish={onFinish}
                onSsoClick={handleSsoLogin}
            />
        </Layout>
    );
};
export const Route: RouteType = {
    path: RouteBuilder.compute('login.default'),
    content: <Login />,
    exact: true,
};
